/**
 * Timeline Service
 *
 * Global singleton providing a shared current time that layers can subscribe to.
 * Supports continuous scrubbing and animated playback. Consumers are responsible
 * for mapping continuous time to their own granularity and memoizing re-renders.
 */

// Playback duration: 0 = real-time, positive number = seconds to play through full range.
// 90s is the shortest (fastest) playback — anything quicker animates too jankily to read.
export type PlaybackDuration = 0 | 90 | 120 | 180

export const PLAYBACK_OPTIONS: readonly PlaybackDuration[] = [0, 90, 120, 180] as const

export const PLAYBACK_LABELS: Record<PlaybackDuration, string> = {
  0: 'Real',
  90: '90s',
  120: '2m',
  180: '3m',
}

export interface TimelineState {
  startTime: Date
  endTime: Date
  currentTime: Date
  playbackDuration: PlaybackDuration
  isPlaying: boolean
  isLooping: boolean
}

export type TimelineListener = (state: Readonly<TimelineState>) => void

class TimelineService {
  private state: TimelineState
  private listeners = new Set<TimelineListener>()
  private animationFrameId: number | undefined
  private lastFrameTime: number | undefined

  private wasPlayingBeforeHidden = false

  constructor() {
    const now = new Date()
    const floored = new Date(now)
    floored.setMinutes(0, 0, 0)

    const endTime = new Date(floored.getTime() + 18 * 60 * 60 * 1000)
    this.state = {
      startTime: new Date(endTime.getTime() - 24 * 60 * 60 * 1000),
      endTime,
      currentTime: new Date(now),
      playbackDuration: 90,
      isPlaying: false,
      isLooping: false,
    }

    // Pause playback when app is hidden to save CPU
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && this.state.isPlaying) {
          this.wasPlayingBeforeHidden = true
          this.pause()
        } else if (document.visibilityState === 'visible' && this.wasPlayingBeforeHidden) {
          this.wasPlayingBeforeHidden = false
          this.play()
        }
      })
    }
  }

  getState(): Readonly<TimelineState> {
    return this.state
  }

  getCurrentTimeISO(): string {
    return this.state.currentTime.toISOString()
  }

  getCurrentDateISO(): string {
    return this.state.currentTime.toISOString().split('T')[0]
  }

  // --- Mutations ---

  setTimeRange(start: Date, end: Date): void {
    if (end <= start) {
      return
    }
    this.state.startTime = start
    this.state.endTime = end
    // Clamp currentTime within new range
    if (this.state.currentTime < start) {
      this.state.currentTime = new Date(start)
    }
    if (this.state.currentTime > end) {
      this.state.currentTime = new Date(end)
    }
    this.notify()
  }

  setCurrentTime(time: Date): void {
    const clamped = new Date(
      Math.max(
        this.state.startTime.getTime(),
        Math.min(this.state.endTime.getTime(), time.getTime())
      )
    )
    this.state.currentTime = clamped
    this.notify()
  }

  setPlaybackDuration(duration: PlaybackDuration): void {
    this.state.playbackDuration = duration
    this.notify()
  }

  cyclePlaybackDuration(): void {
    const currentIdx = PLAYBACK_OPTIONS.indexOf(this.state.playbackDuration)
    const nextIdx = (currentIdx + 1) % PLAYBACK_OPTIONS.length
    this.state.playbackDuration = PLAYBACK_OPTIONS[nextIdx]
    this.notify()
  }

  play(): void {
    if (this.state.isPlaying) {
      return
    }
    // If at end, reset to start before playing
    if (this.state.currentTime >= this.state.endTime) {
      this.state.currentTime = new Date(this.state.startTime)
    }
    this.state.isPlaying = true
    this.lastFrameTime = performance.now()
    this.animationFrameId = requestAnimationFrame(this.tick)
    this.notify()
  }

  pause(): void {
    if (!this.state.isPlaying) {
      return
    }
    this.state.isPlaying = false
    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = undefined
    }
    this.lastFrameTime = undefined
    this.notify()
  }

  togglePlayPause(): void {
    if (this.state.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  }

  reset(): void {
    this.pause()
    this.state.currentTime = new Date(this.state.startTime)
    this.notify()
  }

  toggleLoop(): void {
    this.state.isLooping = !this.state.isLooping
    this.notify()
  }

  // --- Animation loop ---

  private tick = (now: number): void => {
    if (!this.state.isPlaying) {
      return
    }

    if (this.lastFrameTime !== undefined) {
      const elapsedMs = now - this.lastFrameTime
      const rangeMs = this.state.endTime.getTime() - this.state.startTime.getTime()
      // duration=0 means real-time (1:1), otherwise play full range in N seconds
      const simulatedMs =
        this.state.playbackDuration === 0
          ? elapsedMs
          : elapsedMs * (rangeMs / (this.state.playbackDuration * 1000))
      const newTime = this.state.currentTime.getTime() + simulatedMs

      if (newTime >= this.state.endTime.getTime()) {
        if (this.state.isLooping) {
          this.state.currentTime = new Date(this.state.startTime)
        } else {
          this.state.currentTime = new Date(this.state.endTime)
          this.state.isPlaying = false
          this.lastFrameTime = undefined
          this.notify()
          return
        }
      } else {
        this.state.currentTime = new Date(newTime)
      }
    }

    this.lastFrameTime = now
    this.notify()
    this.animationFrameId = requestAnimationFrame(this.tick)
  }

  // --- Listener management ---

  addListener(listener: TimelineListener): void {
    this.listeners.add(listener)
  }

  removeListener(listener: TimelineListener): void {
    this.listeners.delete(listener)
  }

  private notify(): void {
    const snapshot = this.getState()
    for (const listener of this.listeners) {
      listener(snapshot)
    }
  }

  dispose(): void {
    this.pause()
    this.listeners.clear()
  }
}

export const timelineService = new TimelineService()
